import { cleanFieldDraftValue } from "./field-draft";
/**
 * Contexto operativo liviano para Renata (producto/pedido en edición, etc.).
 * Lo setean las pantallas CRUD; el FAB/chat lo lee al armar el mensaje.
 */
export type RenataWorkspaceFocus = {
  route?: string;
  entity?: string;
  entityId?: string | null;
  entityName?: string | null;
  selection?: string[];
  /** Campos del draft visibles para proponer cambios (no secretos). */
  draft?: Record<string, unknown>;
  updatedAt?: number;
  /** Campo específico que se está editando (e.g. 'description', 'longDescription'). */
  field?: string;
  /** Intent del launcher (e.g. 'improve_text', 'rewrite', 'expand'). */
  intent?: string;
};

export type RenataPendingFieldApply = {
  field: string;
  value: string;
  entity?: string;
  entityId?: string | null;
  at: number;
};

let focus: RenataWorkspaceFocus | null = null;
let pendingFieldApply: RenataPendingFieldApply | null = null;
let pendingPrompt: string | null = null;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

export function setRenataWorkspaceFocus(next: RenataWorkspaceFocus | null): void {
  focus = next ? { ...next, updatedAt: Date.now() } : null;
  notify();
}

export function getRenataWorkspaceFocus(): RenataWorkspaceFocus | null {
  return focus;
}

export function clearRenataWorkspaceFocus(): void {
  setRenataWorkspaceFocus(null);
}

export function subscribeRenataWorkspaceFocus(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Queue + broadcast apply so it works even if the form listener remounts. */
export function applyRenataFieldToForm(detail: {
  field: string;
  value: string;
  entity?: string;
  entityId?: string | null;
}): void {
  if (!detail.field || typeof detail.value !== "string") return;
  const cleaned = cleanFieldDraftValue(detail.value) ?? detail.value.trim();
  if (!cleaned) return;
  pendingFieldApply = {
    field: detail.field,
    value: cleaned,
    entity: detail.entity ?? focus?.entity,
    entityId: detail.entityId ?? focus?.entityId ?? null,
    at: Date.now(),
  };
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("sm-renata-apply-field", {
        detail: {
          field: pendingFieldApply.field,
          value: pendingFieldApply.value,
        },
      }),
    );
  }
}

export function getRenataPendingFieldApply(): RenataPendingFieldApply | null {
  return pendingFieldApply;
}

/** Consume pending apply (optionally matching entity). */
export function consumeRenataFieldApply(match?: {
  entity?: string;
  entityId?: string | null;
}): RenataPendingFieldApply | null {
  if (!pendingFieldApply) return null;
  if (match?.entity && pendingFieldApply.entity && pendingFieldApply.entity !== match.entity) {
    return null;
  }
  if (
    match?.entityId != null &&
    pendingFieldApply.entityId != null &&
    match.entityId !== "" &&
    pendingFieldApply.entityId !== match.entityId
  ) {
    return null;
  }
  const out = pendingFieldApply;
  pendingFieldApply = null;
  notify();
  return out;
}

/** One-shot prompt seeded by RenataLauncher (e.g. create empty field). */
export function setRenataPendingPrompt(prompt: string | null): void {
  pendingPrompt = prompt && prompt.trim() ? prompt.trim() : null;
}

export function consumeRenataPendingPrompt(): string | null {
  const out = pendingPrompt;
  pendingPrompt = null;
  return out;
}
